import { useEffect, useMemo, useState, type ReactNode } from "react";
import {
  Search,
  RefreshCw,
  ExternalLink,
  ChevronUp,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Briefcase,
  Sparkles,
  FolderOpen,
  UserCheck,
  UserX,
  Users,
  DollarSign,
  Filter,
  X,
  Eye,
  Bot,
  Zap,
  Globe,
  Star,
  TrendingUp,
  ArrowUpDown,
  Phone,
  CreditCard,
  CheckCircle2,
  XCircle,
  Clock,
  MapPin,
  Tag,
  Award,
  Target,
  Activity,
  FileText,
  Loader2,
} from "lucide-react";
import { useUpworkJobsStore, type UpworkJob } from "../store/useupworkjobsstore";
import { useAuthStore } from "../store/useAuthStore";

type SortKey =
  | "Job Title"
  | "Budget"
  | "Last Viewed by Client"
  | "Client Rating"
  | "Total Applicants"
  | "AI Score"
  | "Total Money Spent"
  | "Hiring Success Rate";

type SortDir = "asc" | "desc";

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

const parseBudget = (str?: string) => {
  if (!str) return { min: 0, max: 0, mid: 0, display: "—" };
  const nums = str.match(/[\d.]+/g)?.map(Number) ?? [];
  if (!nums.length) return { min: 0, max: 0, mid: 0, display: str };

  const a = nums[0] as number;
  if (nums.length === 1) {
    return { min: a, max: a, mid: a, display: `$${a.toLocaleString()}` };
  }
  const b = nums[1] as number;
  return {
    min: a,
    max: b,
    mid: (a + b) / 2,
    display: `$${a.toLocaleString()}–$${b.toLocaleString()}`,
  };
};

const parseMoney = (str?: string) => {
  if (!str) return 0;
  const cleaned = str.replace(/[^\d.]/g, "");
  return Number(cleaned) || 0;
};

const formatMoney = (str?: string) => {
  const n = parseMoney(str);
  if (!n) return "$0";
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(1)}k`;
  return `$${n.toLocaleString()}`;
};

const parseSkills = (str?: string) =>
  (str ?? "")
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);

const parseAiScore = (val: any): number => {
  if (val === null || val === undefined || val === "") return 0;
  if (typeof val === "number") return val;
  const m = String(val).match(/[\d.]+/);
  return m ? Number(m[0]) : 0;
};

const getAIBadge = (score: number) => {
  if (score >= 70)
    return { emoji: "🟢", label: "Good opportunity", color: "text-emerald-700 bg-emerald-50 border-emerald-200" };
  if (score >= 40)
    return { emoji: "🟡", label: "Moderate opportunity", color: "text-amber-700 bg-amber-50 border-amber-200" };
  if (score > 0)
    return { emoji: "🔴", label: "Poor opportunity", color: "text-red-700 bg-red-50 border-red-200" };
  return { emoji: "⚪", label: "Not analyzed", color: "text-gray-600 bg-gray-50 border-gray-200" };
};

const getStatusClasses = (status?: string) => {
  const map: Record<string, string> = {
    ACTIVE: "bg-emerald-50 text-emerald-700 border-emerald-200",
    OPEN: "bg-emerald-50 text-emerald-700 border-emerald-200",
    NEW: "bg-blue-50 text-blue-700 border-blue-200",
    APPLIED: "bg-violet-50 text-violet-700 border-violet-200",
    HIRED: "bg-green-50 text-green-700 border-green-200",
    LOST: "bg-red-50 text-red-700 border-red-200",
    CLOSED: "bg-gray-100 text-gray-500 border-gray-200",
  };
  return map[(status || "").toUpperCase()] || "bg-gray-100 text-gray-600 border-gray-200";
};

const getDecisionClasses = (decision?: string) => {
  const map: Record<string, string> = {
    Accept: "bg-emerald-50 text-emerald-700 border-emerald-200",
    Apply:  "bg-emerald-50 text-emerald-700 border-emerald-200",
    Maybe:  "bg-amber-50 text-amber-700 border-amber-200",
    Reject: "bg-red-50 text-red-700 border-red-200",
    Skip:   "bg-red-50 text-red-700 border-red-200",
  };
  return map[decision ?? ""] || "bg-gray-100 text-gray-500 border-gray-200";
};

const timeAgo = (date?: string | null) => {
  if (!date) return "—";
  const diff = Date.now() - new Date(date).getTime();
  if (Number.isNaN(diff)) return "—";
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  return `${months}mo ago`;
};

const formatDate = (date?: string | null) => {
  if (!date) return "—";
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const isYes = (v?: string) => v?.toLowerCase() === "yes";

const UpworkJobsPage = () => {
  const {
    jobs,
    analytics,
    isFetching,
    isReanalyzing,
    fetchJobs,
    fetchAnalytics,
    reanalyzeJob,
    reanalyzeOpenJobs,
  } = useUpworkJobsStore();

  const [statusFilter, setStatusFilter] = useState("");
  const [decisionFilter, setDecisionFilter] = useState("");
  const [contractFilter, setContractFilter] = useState("");
  const [tierFilter, setTierFilter] = useState("");
  const [countryFilter, setCountryFilter] = useState("");
  const [budgetMin, setBudgetMin] = useState<number | "">("");
  const [budgetMax, setBudgetMax] = useState<number | "">("");
  const [applicantsMin, setApplicantsMin] = useState<number | "">("");
  const [applicantsMax, setApplicantsMax] = useState<number | "">("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [showFilters, setShowFilters] = useState(true);

  const [sortKey, setSortKey] = useState<SortKey>("Last Viewed by Client");
  const [sortDir, setSortDir] = useState<SortDir>("desc");

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const [viewing, setViewing] = useState<UpworkJob | null>(null);

  const [urlInput, setUrlInput] = useState("");

  useEffect(() => {
    fetchJobs();
    fetchAnalytics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setPage(1);
  }, [
    statusFilter,
    decisionFilter,
    contractFilter,
    tierFilter,
    countryFilter,
    budgetMin,
    budgetMax,
    applicantsMin,
    applicantsMax,
    dateFrom,
    dateTo,
    searchQuery,
  ]);

  const countryOptions = useMemo(() => {
    const set = new Set<string>();
    jobs.forEach((j) => j["Country"] && set.add(j["Country"]!));
    return Array.from(set).sort();
  }, [jobs]);

  const filtered = useMemo(() => {
    return jobs.filter((j) => {
      if (statusFilter && j["Job Status"] !== statusFilter) return false;
      if (decisionFilter && j.ai?.["Final Decision"] !== decisionFilter) return false;
      if (contractFilter && j["Contract Type"] !== contractFilter) return false;
      if (tierFilter && j["Contractor Tier"] !== tierFilter) return false;
      if (countryFilter && j["Country"] !== countryFilter) return false;

      const budget = parseBudget(j["Budget Amount"]);
      if (budgetMin !== "" && budget.mid < Number(budgetMin)) return false;
      if (budgetMax !== "" && budget.mid > Number(budgetMax)) return false;

      const apps = j["Total Applicants"] ?? 0;
      if (applicantsMin !== "" && apps < Number(applicantsMin)) return false;
      if (applicantsMax !== "" && apps > Number(applicantsMax)) return false;

      const lv = j["Last Viewed by Client"];
      if (dateFrom && lv && new Date(lv) < new Date(dateFrom)) return false;
      if (dateTo && lv && new Date(lv) > new Date(dateTo + "T23:59:59")) return false;

      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        const haystack = [
          j["Job Title"],
          j["Country"],
          j["Category"],
          j["Skills and Expertise"],
          j["Job Description"],
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      return true;
    });
  }, [
    jobs,
    statusFilter,
    decisionFilter,
    contractFilter,
    tierFilter,
    countryFilter,
    budgetMin,
    budgetMax,
    applicantsMin,
    applicantsMax,
    dateFrom,
    dateTo,
    searchQuery,
  ]);

  const sorted = useMemo(() => {
    const copy = [...filtered];
    copy.sort((a, b) => {
      let aVal: number | string = 0;
      let bVal: number | string = 0;
      switch (sortKey) {
        case "Job Title":
          aVal = (a["Job Title"] || "").toLowerCase();
          bVal = (b["Job Title"] || "").toLowerCase();
          break;
        case "Budget":
          aVal = parseBudget(a["Budget Amount"]).mid;
          bVal = parseBudget(b["Budget Amount"]).mid;
          break;
        case "Last Viewed by Client":
          aVal = a["Last Viewed by Client"] ? new Date(a["Last Viewed by Client"]!).getTime() : 0;
          bVal = b["Last Viewed by Client"] ? new Date(b["Last Viewed by Client"]!).getTime() : 0;
          break;
        case "Client Rating":
          aVal = a["Client Rating"] ?? 0;
          bVal = b["Client Rating"] ?? 0;
          break;
        case "Total Applicants":
          aVal = a["Total Applicants"] ?? 0;
          bVal = b["Total Applicants"] ?? 0;
          break;
        case "AI Score":
          aVal = parseAiScore(a.ai?.["Weighted Scoring Score"]);
          bVal = parseAiScore(b.ai?.["Weighted Scoring Score"]);
          break;
        case "Total Money Spent":
          aVal = parseMoney(a["Total Money Spent"]);
          bVal = parseMoney(b["Total Money Spent"]);
          break;
        case "Hiring Success Rate":
          aVal = a["Hiring Success Rate"] ?? 0;
          bVal = b["Hiring Success Rate"] ?? 0;
          break;
      }
      if (aVal < bVal) return sortDir === "asc" ? -1 : 1;
      if (aVal > bVal) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
    return copy;
  }, [filtered, sortKey, sortDir]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const paged = sorted.slice((page - 1) * pageSize, page * pageSize);

  const handleSort = (key: SortKey) => {
    if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortKey(key);
      setSortDir("desc");
    }
  };

  const SortIcon = ({ col }: { col: SortKey }) => {
    if (sortKey !== col) return <ArrowUpDown size={13} className="text-gray-400 ml-1 inline" />;
    return sortDir === "asc" ? (
      <ChevronUp size={13} className="text-[#2462b2] ml-1 inline" />
    ) : (
      <ChevronDown size={13} className="text-[#2462b2] ml-1 inline" />
    );
  };

  const clearFilters = () => {
    setStatusFilter("");
    setDecisionFilter("");
    setContractFilter("");
    setTierFilter("");
    setCountryFilter("");
    setBudgetMin("");
    setBudgetMax("");
    setApplicantsMin("");
    setApplicantsMax("");
    setDateFrom("");
    setDateTo("");
    setSearchQuery("");
  };

  const hasActiveFilters =
    statusFilter ||
    decisionFilter ||
    contractFilter ||
    tierFilter ||
    countryFilter ||
    budgetMin !== "" ||
    budgetMax !== "" ||
    applicantsMin !== "" ||
    applicantsMax !== "" ||
    dateFrom ||
    dateTo ||
    searchQuery;

  const cards = [
    {
      label: "New Jobs (24h)",
      value: analytics?.newJobs ?? 0,
      icon: Briefcase,
      accent: "from-blue-500 to-blue-600",
    },
    {
      label: "AI Recommended",
      value: analytics?.aiRecommended ?? 0,
      icon: Sparkles,
      accent: "from-violet-500 to-purple-600",
    },
    {
      label: "Active Jobs",
      value: analytics?.active ?? 0,
      icon: FolderOpen,
      accent: "from-emerald-500 to-green-600",
    },
    {
      label: "AI Pending",
      value: analytics?.aiPending ?? 0,
      icon: Clock,
      accent: "from-sky-500 to-cyan-600",
    },
    {
      label: "AI Rejected",
      value: analytics?.aiRejected ?? 0,
      icon: UserX,
      accent: "from-red-500 to-rose-600",
    },
    {
      label: "High Competition",
      value: analytics?.highCompetition ?? 0,
      icon: Users,
      accent: "from-amber-500 to-orange-600",
    },
    {
      label: "Total Jobs",
      value: analytics?.total ?? jobs.length,
      icon: UserCheck,
      accent: "from-green-500 to-emerald-600",
    },
    {
      label: "Avg Applicants",
      value: analytics?.avgApplicants ?? 0,
      icon: DollarSign,
      accent: "from-teal-500 to-cyan-600",
    },
  ];

  const { authUser } = useAuthStore();
  const isBD = authUser?.role === "Business Developer";

  return (
    <div className={`min-h-screen px-4 lg:px-8 pb-10 bg-background ${isBD ? "pt-8" : "pt-20"}`}>
      <div className="max-w-[1600px] mx-auto">

        <section className="bg-surface-container-lowest rounded-xl border border-outline-variant card-shadow p-6 mb-6">
          <div className="flex items-center gap-3 mb-1">
            <div className="w-10 h-10 grid place-items-center rounded-xl bg-primary/10 text-primary">
              <Zap size={20} />
            </div>
            <h1 className="text-[26px] font-bold text-on-surface tracking-tight">
              Upwork Jobs Intelligence
            </h1>
          </div>
          <p className="text-[14px] text-on-surface-variant ml-[52px]">
            AI-powered job tracking, analysis &amp; recommendations for Business Development
          </p>
        </section>

        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3 mb-6">
          {cards.map((card) => (
            <div
              key={card.label}
              className="bg-white/90 backdrop-blur rounded-2xl border border-gray-200/60 p-4 hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200 group cursor-default"
            >
              <div className="flex items-center justify-between mb-3">
                <div className={`p-2 rounded-lg bg-gradient-to-br ${card.accent} shadow-sm`}>
                  <card.icon size={16} className="text-white" />
                </div>
              </div>
              <p className="text-2xl font-bold text-gray-900">{card.value}</p>
              <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider mt-1">
                {card.label}
              </p>
            </div>
          ))}
        </div>

        <div className="bg-white/95 backdrop-blur-2xl rounded-3xl shadow-2xl border border-white/20 p-5 lg:p-8">

          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
            <div className="flex items-center gap-2 flex-1 max-w-lg">
              <div className="relative flex-1">
                <Globe size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  className="w-full pl-9 pr-4 py-2.5 text-sm border border-gray-200 rounded-xl bg-gray-50/80 focus:outline-none focus:ring-2 focus:ring-[#2462b2]/20 focus:border-[#2462b2]/40 transition placeholder:text-gray-400"
                  placeholder="Paste Upwork job URL to search..."
                  value={urlInput}
                  onChange={(e) => setUrlInput(e.target.value)}
                />
              </div>
              <button className="px-4 py-2.5 text-sm font-semibold rounded-xl bg-gradient-to-r from-[#2462b2] to-[#1e55a0] text-white shadow hover:shadow-lg hover:scale-[1.02] transition-all whitespace-nowrap">
                <Search size={15} className="inline -mt-0.5 mr-1.5" />
                Search
              </button>
            </div>

            <div className="flex items-center gap-2 flex-wrap">
              <button
                onClick={() => reanalyzeOpenJobs()}
                disabled={isReanalyzing}
                className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-semibold rounded-xl border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 hover:border-gray-300 transition-all shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isReanalyzing ? (
                  <Loader2 size={15} className="animate-spin text-violet-500" />
                ) : (
                  <Bot size={15} className="text-violet-500" />
                )}
                Reanalyze Active Jobs
              </button>
              <button
                onClick={() => {
                  fetchJobs();
                  fetchAnalytics();
                }}
                disabled={isFetching}
                className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-semibold rounded-xl border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 hover:border-gray-300 transition-all shadow-sm disabled:opacity-50"
              >
                {isFetching ? (
                  <Loader2 size={15} className="animate-spin text-emerald-500" />
                ) : (
                  <RefreshCw size={15} className="text-emerald-500" />
                )}
                Refresh
              </button>
              <button
                onClick={() => setShowFilters((v) => !v)}
                className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-semibold rounded-xl border transition-all shadow-sm ${
                  showFilters
                    ? "border-[#2462b2]/30 bg-blue-50 text-[#2462b2]"
                    : "border-gray-200 bg-white text-gray-700 hover:bg-gray-50"
                }`}
              >
                <Filter size={15} />
                Filters
                {hasActiveFilters && <span className="w-2 h-2 rounded-full bg-[#2462b2] animate-pulse" />}
              </button>
            </div>
          </div>

          {showFilters && (
            <div className="mb-6 p-4 rounded-2xl bg-gradient-to-r from-gray-50/80 to-slate-50/80 border border-gray-100">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-6 gap-3 mb-3">
                <FilterField label="Job Status">
                  <select
                    className="ff-input"
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                  >
                    <option value="">All Statuses</option>
                    <option value="ACTIVE">Active</option>
                    <option value="CLOSED">Closed</option>
                  </select>
                </FilterField>

                <FilterField label="AI Decision">
                  <select
                    className="ff-input"
                    value={decisionFilter}
                    onChange={(e) => setDecisionFilter(e.target.value)}
                  >
                    <option value="">All Decisions</option>
                    <option value="Accept">Accept</option>
                    <option value="Maybe">Maybe</option>
                    <option value="Reject">Reject</option>
                  </select>
                </FilterField>

                <FilterField label="Contract Type">
                  <select
                    className="ff-input"
                    value={contractFilter}
                    onChange={(e) => setContractFilter(e.target.value)}
                  >
                    <option value="">All Types</option>
                    <option value="HOURLY">Hourly</option>
                    <option value="FIXED">Fixed</option>
                  </select>
                </FilterField>

                <FilterField label="Tier">
                  <select
                    className="ff-input"
                    value={tierFilter}
                    onChange={(e) => setTierFilter(e.target.value)}
                  >
                    <option value="">All Tiers</option>
                    <option value="ENTRY">Entry</option>
                    <option value="INTERMEDIATE">Intermediate</option>
                    <option value="EXPERT">Expert</option>
                  </select>
                </FilterField>

                <FilterField label="Country">
                  <select
                    className="ff-input"
                    value={countryFilter}
                    onChange={(e) => setCountryFilter(e.target.value)}
                  >
                    <option value="">All Countries</option>
                    {countryOptions.map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </select>
                </FilterField>

                <FilterField label="Budget Min ($)">
                  <input
                    className="ff-input"
                    type="number"
                    placeholder="$0"
                    value={budgetMin as any}
                    onChange={(e) => setBudgetMin(e.target.value === "" ? "" : Number(e.target.value))}
                  />
                </FilterField>

                <FilterField label="Budget Max ($)">
                  <input
                    className="ff-input"
                    type="number"
                    placeholder="$∞"
                    value={budgetMax as any}
                    onChange={(e) => setBudgetMax(e.target.value === "" ? "" : Number(e.target.value))}
                  />
                </FilterField>

                <FilterField label="Applicants Min">
                  <input
                    className="ff-input"
                    type="number"
                    placeholder="0"
                    value={applicantsMin as any}
                    onChange={(e) => setApplicantsMin(e.target.value === "" ? "" : Number(e.target.value))}
                  />
                </FilterField>

                <FilterField label="Applicants Max">
                  <input
                    className="ff-input"
                    type="number"
                    placeholder="∞"
                    value={applicantsMax as any}
                    onChange={(e) => setApplicantsMax(e.target.value === "" ? "" : Number(e.target.value))}
                  />
                </FilterField>

                <FilterField label="Last Viewed From">
                  <input
                    className="ff-input"
                    type="date"
                    value={dateFrom}
                    onChange={(e) => setDateFrom(e.target.value)}
                  />
                </FilterField>

                <FilterField label="Last Viewed To">
                  <input
                    className="ff-input"
                    type="date"
                    value={dateTo}
                    onChange={(e) => setDateTo(e.target.value)}
                  />
                </FilterField>
              </div>

              <div className="flex items-center gap-3">
                <div className="relative flex-1">
                  <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    className="w-full pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-[#2462b2]/20 focus:border-[#2462b2]/40 transition placeholder:text-gray-400"
                    placeholder="Search by title, country, category, skills, or description..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
                {hasActiveFilters && (
                  <button
                    onClick={clearFilters}
                    className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-red-600 hover:bg-red-50 rounded-xl transition"
                  >
                    <X size={14} /> Clear All
                  </button>
                )}
              </div>
            </div>
          )}

          <div className="flex items-center justify-between mb-4">
            <p className="text-sm text-gray-500">
              Showing <span className="font-bold text-gray-800">{paged.length}</span> of{" "}
              <span className="font-bold text-gray-800">{filtered.length}</span> jobs
              {filtered.length !== jobs.length && (
                <span className="text-gray-400"> (filtered from {jobs.length})</span>
              )}
            </p>
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <span>Rows:</span>
              <select
                className="px-2 py-1 border border-gray-200 rounded-lg bg-white text-sm focus:outline-none"
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setPage(1);
                }}
              >
                {PAGE_SIZE_OPTIONS.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="overflow-x-auto border border-gray-200/80 rounded-2xl">
            <table className="w-full table-auto text-sm">
              <thead>
                <tr className="bg-gradient-to-r from-gray-50 to-slate-50 text-[11px] font-bold text-gray-500 uppercase tracking-wider">
                  <Th onClick={() => handleSort("Job Title")}>Job Title <SortIcon col="Job Title" /></Th>
                  <Th onClick={() => handleSort("Budget")}>Budget <SortIcon col="Budget" /></Th>
                  <Th>Type</Th>
                  <Th>Tier</Th>
                  <Th>Category</Th>
                  <Th>Country</Th>
                  <Th>Verified</Th>
                  <Th onClick={() => handleSort("Client Rating")}>Rating <SortIcon col="Client Rating" /></Th>
                  <Th onClick={() => handleSort("Total Money Spent")}>Spent <SortIcon col="Total Money Spent" /></Th>
                  <Th onClick={() => handleSort("Hiring Success Rate")}>Hire % <SortIcon col="Hiring Success Rate" /></Th>
                  <Th onClick={() => handleSort("Total Applicants")}>Apps <SortIcon col="Total Applicants" /></Th>
                  <Th>Invites</Th>
                  <Th onClick={() => handleSort("AI Score")}>AI Score <SortIcon col="AI Score" /></Th>
                  <Th>AI Decision</Th>
                  <Th>Status</Th>
                  <Th onClick={() => handleSort("Last Viewed by Client")}>Last Viewed <SortIcon col="Last Viewed by Client" /></Th>
                  <th className="px-3 py-3.5 text-center">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {isFetching && jobs.length === 0 && (
                  <tr>
                    <td colSpan={17} className="text-center py-16">
                      <Loader2 size={32} className="animate-spin text-[#2462b2] mx-auto mb-2" />
                      <p className="text-sm text-gray-500 font-medium">Loading jobs...</p>
                    </td>
                  </tr>
                )}

                {!isFetching && paged.length === 0 && (
                  <tr>
                    <td colSpan={17} className="text-center py-16">
                      <div className="flex flex-col items-center gap-2">
                        <Search size={40} className="text-gray-300" />
                        <p className="text-gray-500 font-medium">
                          {jobs.length === 0 ? "No jobs found" : "No jobs match your filters"}
                        </p>
                        {hasActiveFilters && (
                          <button
                            onClick={clearFilters}
                            className="text-sm text-[#2462b2] font-semibold hover:underline"
                          >
                            Clear all filters
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                )}

                {paged.map((job) => {
                  const skills = parseSkills(job["Skills and Expertise"]);
                  const aiScore = parseAiScore(job.ai?.["Weighted Scoring Score"]);
                  const badge = getAIBadge(aiScore);
                  const decision = job.ai?.["Final Decision"] || "—";
                  const budget = parseBudget(job["Budget Amount"]);
                  const apps = job["Total Applicants"] ?? 0;

                  return (
                    <tr key={job._id} className="hover:bg-blue-50/40 transition-colors group">

                      <td className="px-3 py-3 max-w-[280px]">
                        <div className="flex items-start gap-2">
                          <span className="text-base mt-0.5" title={badge.label}>
                            {badge.emoji}
                          </span>
                          <div className="min-w-0">
                            <p className="text-sm font-semibold text-gray-900 truncate group-hover:text-[#2462b2] transition">
                              {job["Job Title"]}
                            </p>
                            {skills.length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-1">
                                {skills.slice(0, 3).map((s) => (
                                  <span
                                    key={s}
                                    className="text-[10px] font-medium px-1.5 py-0.5 rounded-md bg-gray-100 text-gray-500"
                                  >
                                    {s}
                                  </span>
                                ))}
                                {skills.length > 3 && (
                                  <span className="text-[10px] font-medium text-gray-400">
                                    +{skills.length - 3}
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>

                      <td className="px-3 py-3 text-sm font-semibold text-gray-800 whitespace-nowrap">
                        {budget.display}
                      </td>

                      <td className="px-3 py-3 whitespace-nowrap">
                        <span className="text-xs font-semibold text-gray-600">
                          {job["Contract Type"] || "—"}
                        </span>
                      </td>

                      <td className="px-3 py-3 whitespace-nowrap">
                        <span className="text-xs font-medium text-gray-500 capitalize">
                          {(job["Contractor Tier"] || "—").toLowerCase()}
                        </span>
                      </td>

                      <td className="px-3 py-3 max-w-[160px]">
                        <p className="text-xs text-gray-600 truncate" title={job["Category"]}>
                          {job["Category"] || "—"}
                        </p>
                      </td>

                      <td className="px-3 py-3 text-sm text-gray-600 whitespace-nowrap">
                        {job["Country"] || "—"}
                      </td>

                      <td className="px-3 py-3 whitespace-nowrap">
                        <div className="flex items-center gap-1">
                          <Phone
                            size={13}
                            className={isYes(job["Phone Verification"]) ? "text-emerald-500" : "text-gray-300"}
                          />
                          <CreditCard
                            size={13}
                            className={isYes(job["Payment Verification"]) ? "text-emerald-500" : "text-gray-300"}
                          />
                        </div>
                      </td>

                      <td className="px-3 py-3 whitespace-nowrap">
                        {job["Client Rating"] ? (
                          <div className="flex flex-col">
                            <span className="inline-flex items-center gap-1 text-sm font-medium text-gray-800">
                              <Star size={12} className="text-amber-400 fill-amber-400" />
                              {job["Client Rating"]?.toFixed(1)}
                            </span>
                            {!!job["Number of Reviews"] && (
                              <span className="text-[10px] text-gray-400">
                                {job["Number of Reviews"]} reviews
                              </span>
                            )}
                          </div>
                        ) : (
                          <span className="text-xs text-gray-400">N/A</span>
                        )}
                      </td>

                      <td className="px-3 py-3 text-sm font-medium text-gray-700 whitespace-nowrap">
                        {formatMoney(job["Total Money Spent"])}
                      </td>

                      <td className="px-3 py-3 text-sm text-gray-700 whitespace-nowrap">
                        {job["Hiring Success Rate"] ? `${Math.round(job["Hiring Success Rate"]!)}%` : "—"}
                      </td>

                      <td className="px-3 py-3 text-sm">
                        <span className={`font-semibold ${apps > 50 ? "text-red-600" : "text-gray-700"}`}>
                          {apps}
                        </span>
                      </td>

                      <td className="px-3 py-3 text-sm text-gray-600">{job["Invitations Sent"] ?? 0}</td>

                      <td className="px-3 py-3">
                        {aiScore > 0 ? (
                          <div className="flex items-center gap-2">
                            <div className="w-12 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                              <div
                                className={`h-full rounded-full transition-all ${
                                  aiScore >= 70
                                    ? "bg-emerald-500"
                                    : aiScore >= 40
                                    ? "bg-amber-500"
                                    : "bg-red-500"
                                }`}
                                style={{ width: `${Math.min(100, aiScore)}%` }}
                              />
                            </div>
                            <span className="text-sm font-bold text-gray-700">{Math.round(aiScore)}</span>
                          </div>
                        ) : (
                          <span className="text-xs text-gray-400">Pending</span>
                        )}
                      </td>

                      <td className="px-3 py-3">
                        <span
                          className={`inline-flex items-center gap-1 px-2.5 py-1 text-xs font-bold rounded-lg border ${getDecisionClasses(
                            decision
                          )}`}
                        >
                          {decision === "Accept" && <TrendingUp size={11} />}
                          {decision}
                        </span>
                      </td>

                      <td className="px-3 py-3">
                        <span
                          className={`inline-block px-2.5 py-1 text-[10px] font-bold rounded-lg border ${getStatusClasses(
                            job["Job Status"]
                          )}`}
                        >
                          {job["Job Status"] || "—"}
                        </span>
                      </td>

                      <td className="px-3 py-3 text-sm text-gray-600 whitespace-nowrap">
                        {timeAgo(job["Last Viewed by Client"])}
                      </td>

                      <td className="px-3 py-3 text-center">
                        <div className="flex items-center justify-center gap-1">
                          <button
                            onClick={() => setViewing(job)}
                            className="p-1.5 rounded-lg hover:bg-gray-100 transition text-gray-500 hover:text-[#2462b2]"
                            title="View Details"
                          >
                            <Eye size={15} />
                          </button>
                          <button
                            onClick={() => reanalyzeJob(job._id)}
                            disabled={isReanalyzing}
                            className="p-1.5 rounded-lg hover:bg-gray-100 transition text-gray-500 hover:text-violet-600 disabled:opacity-40"
                            title="Reanalyze with AI"
                          >
                            <Bot size={15} />
                          </button>
                          {job["URL"] && (
                            <a
                              href={job["URL"]}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="p-1.5 rounded-lg hover:bg-gray-100 transition text-gray-500 hover:text-emerald-600"
                              title="Open on Upwork"
                            >
                              <ExternalLink size={15} />
                            </a>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-5">
              <p className="text-sm text-gray-500">
                Page {page} of {totalPages}
              </p>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="p-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition"
                >
                  <ChevronLeft size={16} />
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter((p) => p === 1 || p === totalPages || Math.abs(p - page) <= 1)
                  .reduce<(number | "...")[]>((acc, p, idx, arr) => {
                    if (idx > 0 && p - (arr[idx - 1] as number) > 1) acc.push("...");
                    acc.push(p);
                    return acc;
                  }, [])
                  .map((p, i) =>
                    p === "..." ? (
                      <span key={`dots-${i}`} className="px-2 text-gray-400">
                        ...
                      </span>
                    ) : (
                      <button
                        key={p}
                        onClick={() => setPage(p as number)}
                        className={`w-9 h-9 rounded-lg text-sm font-semibold transition ${
                          page === p
                            ? "bg-gradient-to-r from-[#2462b2] to-[#1e55a0] text-white shadow"
                            : "border border-gray-200 text-gray-600 hover:bg-gray-50"
                        }`}
                      >
                        {p}
                      </button>
                    )
                  )}
                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="p-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition"
                >
                  <ChevronRight size={16} />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {viewing && <ViewJobModal job={viewing} onClose={() => setViewing(null)} onReanalyze={reanalyzeJob} />}

      <style>{`
        .ff-input {
          width: 100%;
          padding: 0.5rem 0.75rem;
          font-size: 0.875rem;
          background: #fff;
          border: 1px solid #e5e7eb;
          border-radius: 0.75rem;
          transition: all 0.15s;
        }
        .ff-input:focus {
          outline: none;
          border-color: rgba(36,98,178,0.4);
          box-shadow: 0 0 0 3px rgba(36,98,178,0.15);
        }
      `}</style>
    </div>
  );
};

export default UpworkJobsPage;

const FilterField = ({ label, children }: { label: string; children: ReactNode }) => (
  <div>
    <label className="block text-[11px] font-bold text-gray-500 uppercase tracking-wider mb-1.5">
      {label}
    </label>
    {children}
  </div>
);

const Th = ({
  children,
  onClick,
}: {
  children: ReactNode;
  onClick?: () => void;
}) => (
  <th
    className={`px-3 py-3.5 text-left whitespace-nowrap select-none ${
      onClick ? "cursor-pointer hover:text-[#2462b2] transition" : ""
    }`}
    onClick={onClick}
  >
    {children}
  </th>
);

interface ViewModalProps {
  job: UpworkJob;
  onClose: () => void;
  onReanalyze: (id: string) => void;
}

const ViewJobModal = ({ job, onClose, onReanalyze }: ViewModalProps) => {
  const skills = parseSkills(job["Skills and Expertise"]);
  const budget = parseBudget(job["Budget Amount"]);
  const aiScore = parseAiScore(job.ai?.["Weighted Scoring Score"]);
  const decision = job.ai?.["Final Decision"];

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-3xl shadow-2xl max-w-5xl w-full max-h-[92vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >

        <div className="flex items-start justify-between gap-4 p-6 border-b border-gray-100">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1 text-xs text-gray-500">
              <span>ID: {job["Job ID"]}</span>
              <span>•</span>
              <span>Last viewed {timeAgo(job["Last Viewed by Client"])}</span>
            </div>
            <h2 className="text-xl font-bold text-gray-900 leading-snug">{job["Job Title"]}</h2>
            <div className="flex flex-wrap items-center gap-2 mt-3">
              <Pill className={getStatusClasses(job["Job Status"])}>{job["Job Status"] || "—"}</Pill>
              {decision && <Pill className={getDecisionClasses(decision)}>AI: {decision}</Pill>}
              <Pill className="bg-gray-100 text-gray-700 border-gray-200">
                {job["Contract Type"] || "—"}
              </Pill>
              <Pill className="bg-gray-100 text-gray-700 border-gray-200">
                {(job["Contractor Tier"] || "").toLowerCase() || "—"}
              </Pill>
              <Pill className="bg-blue-50 text-blue-700 border-blue-200">{budget.display}</Pill>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button
              onClick={() => onReanalyze(job._id)}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold rounded-xl border border-violet-200 bg-violet-50 text-violet-700 hover:bg-violet-100 transition"
            >
              <Bot size={15} /> Reanalyze
            </button>
            {job["URL"] && (
              <a
                href={job["URL"]}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold rounded-xl bg-gradient-to-r from-[#2462b2] to-[#1e55a0] text-white hover:shadow-lg transition"
              >
                <ExternalLink size={15} /> Upwork
              </a>
            )}
            <button
              onClick={onClose}
              className="p-2 rounded-xl hover:bg-gray-100 text-gray-500 transition"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        <div className="overflow-y-auto p-6 space-y-6">

          <Section icon={Sparkles} title="AI Analysis" iconColor="text-violet-500">
            {!job.ai ? (
              <div className="p-4 rounded-xl bg-gray-50 text-center text-sm text-gray-500">
                No AI analysis yet for this job.
              </div>
            ) : (
              <div className="space-y-4">

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div className="p-4 rounded-xl border border-gray-200 bg-gradient-to-br from-violet-50 to-purple-50">
                    <p className="text-[11px] font-bold text-gray-500 uppercase tracking-wider mb-1">
                      Final Decision
                    </p>
                    <p className="text-2xl font-extrabold text-gray-900">{decision || "—"}</p>
                  </div>
                  <div className="p-4 rounded-xl border border-gray-200 bg-gradient-to-br from-emerald-50 to-green-50">
                    <p className="text-[11px] font-bold text-gray-500 uppercase tracking-wider mb-1">
                      Weighted Score
                    </p>
                    <p className="text-2xl font-extrabold text-gray-900">
                      {aiScore > 0 ? Math.round(aiScore) : "—"}
                    </p>
                  </div>
                  <div className="p-4 rounded-xl border border-gray-200 bg-gradient-to-br from-amber-50 to-orange-50">
                    <p className="text-[11px] font-bold text-gray-500 uppercase tracking-wider mb-1">
                      Hard Filter
                    </p>
                    <p className="text-2xl font-extrabold text-gray-900">
                      {job.ai["Hard Filter Decision"] || "—"}
                    </p>
                  </div>
                </div>

                {job.ai["Final Synthesis"] && (
                  <ReasoningBlock
                    icon={Target}
                    title="Final Synthesis"
                    text={job.ai["Final Synthesis"]}
                    accent="violet"
                  />
                )}
                {job.ai["Hard Filter Reasoning"] && (
                  <ReasoningBlock
                    icon={CheckCircle2}
                    title="Hard Filter Reasoning"
                    text={job.ai["Hard Filter Reasoning"]}
                    accent="blue"
                  />
                )}
                {job.ai["Budget Guardrail Reasoning"] && (
                  <ReasoningBlock
                    icon={DollarSign}
                    title="Budget Guardrail"
                    text={job.ai["Budget Guardrail Reasoning"]}
                    accent="emerald"
                  />
                )}
                {job.ai["Weighted Scoring Reasoning"] && (
                  <ReasoningBlock
                    icon={Award}
                    title="Weighted Scoring Reasoning"
                    text={job.ai["Weighted Scoring Reasoning"]}
                    accent="amber"
                  />
                )}
                {(job.ai["EV Calculator Decision"] || job.ai["EV Calculator Reasoning"]) && (
                  <ReasoningBlock
                    icon={Activity}
                    title={`EV Calculator${
                      job.ai["EV Calculator Decision"] ? ` — ${job.ai["EV Calculator Decision"]}` : ""
                    }`}
                    text={job.ai["EV Calculator Reasoning"] || ""}
                    accent="rose"
                  />
                )}
              </div>
            )}
          </Section>

          {job["Job Description"] && (
            <Section icon={FileText} title="Job Description">
              <div className="p-4 rounded-xl bg-gray-50 border border-gray-100 text-sm text-gray-700 whitespace-pre-wrap leading-relaxed max-h-72 overflow-y-auto">
                {job["Job Description"]}
              </div>
            </Section>
          )}

          {skills.length > 0 && (
            <Section icon={Tag} title="Skills & Expertise">
              <div className="flex flex-wrap gap-2">
                {skills.map((s) => (
                  <span
                    key={s}
                    className="px-3 py-1.5 text-xs font-semibold rounded-full bg-blue-50 text-[#2462b2] border border-blue-200"
                  >
                    {s}
                  </span>
                ))}
              </div>
            </Section>
          )}

          <Section icon={Briefcase} title="Job Details">
            <DetailGrid
              items={[
                ["Category", job["Category"]],
                ["Category Group", job["Category Group"]],
                ["Contract Type", job["Contract Type"]],
                ["Contractor Tier", job["Contractor Tier"]],
                ["Experience", job["Experience"]],
                ["Budget Amount", job["Budget Amount"]],
                ["Positions to Hire", job["Positions to Hire"]],
                ["Time Zone", job["Time Zone"]],
                ["Premium", job["Premium"] ? "Yes" : "No"],
                ["Phone Verified", job["Phone Verification"] || "—"],
                ["Payment Verified", job["Payment Verification"] || "—"],
                ["Published", formatDate(job["Published"])],
              ]}
            />
          </Section>

          <Section icon={MapPin} title="Client Information">
            <DetailGrid
              items={[
                ["Country", job["Country"]],
                ["City", job["City"]],
                ["Business Industry", job["Business Industry"]],
                [
                  "Client Rating",
                  job["Client Rating"]
                    ? `${job["Client Rating"]?.toFixed(2)} (${job["Number of Reviews"] ?? 0} reviews)`
                    : "—",
                ],
                ["Total Money Spent", job["Total Money Spent"]],
                ["Total Contracts", job["Total Contracts"]],
                ["Total Hired", job["Total Hired"]],
                ["Jobs with Hires", job["Jobs with Hires"]],
                ["Avg. Hourly Rate Paid", job["Avg. Hourly Rate Paid"] ? `$${job["Avg. Hourly Rate Paid"]}/hr` : "—"],
                ["Total Hours Billed", job["Total Hours Billed"]],
                ["Active Job Posts", job["Active Job Posts"]],
                ["Current Active Assignments", job["Current Active Assignments"]],
                [
                  "Hiring Success Rate",
                  job["Hiring Success Rate"] ? `${Math.round(job["Hiring Success Rate"]!)}%` : "—",
                ],
              ]}
            />
          </Section>

          <Section icon={Activity} title="Job Activity">
            <DetailGrid
              items={[
                ["Total Applicants", job["Total Applicants"]],
                ["Total Invited for Interviews", job["Total Invited for Interviews"]],
                ["Invitations Sent", job["Invitations Sent"]],
                ["Last Viewed by Client", formatDate(job["Last Viewed by Client"])],
              ]}
            />
          </Section>
        </div>
      </div>
    </div>
  );
};

const Section = ({
  icon: Icon,
  title,
  iconColor = "text-[#2462b2]",
  children,
}: {
  icon: any;
  title: string;
  iconColor?: string;
  children: ReactNode;
}) => (
  <div>
    <div className="flex items-center gap-2 mb-3">
      <Icon size={16} className={iconColor} />
      <h3 className="text-sm font-bold text-gray-800 uppercase tracking-wider">{title}</h3>
    </div>
    {children}
  </div>
);

const DetailGrid = ({ items }: { items: [string, any][] }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
    {items.map(([label, value]) => (
      <div key={label} className="p-3 rounded-xl bg-gray-50 border border-gray-100">
        <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-0.5">{label}</p>
        <p className="text-sm font-semibold text-gray-800 break-words">
          {value === null || value === undefined || value === "" || value === "None" ? "—" : String(value)}
        </p>
      </div>
    ))}
  </div>
);

const Pill = ({ children, className = "" }: { children: ReactNode; className?: string }) => (
  <span className={`inline-block px-2.5 py-1 text-xs font-bold rounded-lg border capitalize ${className}`}>
    {children}
  </span>
);

const ReasoningBlock = ({
  icon: Icon,
  title,
  text,
  accent,
}: {
  icon: any;
  title: string;
  text: string;
  accent: "violet" | "blue" | "emerald" | "amber" | "rose";
}) => {
  const accents: Record<string, string> = {
    violet: "border-violet-200 bg-violet-50/40 text-violet-700",
    blue: "border-blue-200 bg-blue-50/40 text-blue-700",
    emerald: "border-emerald-200 bg-emerald-50/40 text-emerald-700",
    amber: "border-amber-200 bg-amber-50/40 text-amber-700",
    rose: "border-rose-200 bg-rose-50/40 text-rose-700",
  };
  return (
    <div className={`p-4 rounded-xl border ${accents[accent]}`}>
      <div className="flex items-center gap-2 mb-2">
        <Icon size={14} />
        <h4 className="text-xs font-bold uppercase tracking-wider">{title}</h4>
      </div>
      <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">{text}</p>
    </div>
  );
};
